import type { UseCaseContent } from "@/lib/use-cases/content";

export const ID_USE_CASES: Partial<Record<string, UseCaseContent>> = {
  clippers: {
    related: [
      "/features/auto-clip",
      "/features/video-cutter",
      "/calculators/clips-from-one-video",
      "/calculators/views-to-money",
    ],
    navTitle: "Clipper",
    seoTitle: "Dapat Uang dari Clipping Video: Campaign Clipping",
    seoDescription:
      "Temukan campaign clipping, potong klip viral dengan AI, dan lacak views kamu, semua di dalam Crayo. Cara tercepat mulai jadi clipper dan dibayar.",
    heroPrefix: "Dibayar untuk",
    heroHighlight: "ngeklip video",
    heroSuffix: "dari kreator yang sudah kamu tonton",
    description:
      "Jelajahi campaign clipping yang sedang aktif di Content Library Crayo, potong klip dalam hitungan menit dengan AI, dan lacak setiap view. Cari kerjaannya dan kerjakan di satu aplikasi.",
    buttonText: "Lihat campaign",
    buttonLink: "https://crayo.ai/content-library",
    steps: [
      {
        title: "Cari campaign",
        description:
          "Buka Content Library dan jelajahi campaign aktif dengan reward per klip dan footage sumber yang siap dipakai.",
      },
      {
        title: "Potong dalam hitungan menit",
        description:
          "Auto Clip menemukan momen terkuat, atau pakai Clip It untuk memotong persis yang kamu mau, dengan subtitle yang diterapkan otomatis.",
      },
      {
        title: "Posting dan lacak",
        description:
          "Publikasikan ke akun kamu dan pantau views dengan View Tracker, jadi kamu selalu tahu mana yang perform.",
      },
    ],
    benefits: [
      {
        title: "Campaign datang ke kamu",
        description:
          "Tanpa DM dingin, tanpa berburu di Discord. Campaign aktif ada di dalam Crayo lengkap dengan footage-nya.",
      },
      {
        title: "Momen viral, dicarikan untukmu",
        description:
          "Auto Clip memindai video panjang dan memunculkan momen yang layak diposting, jadi kamu tidak perlu menyisir VOD berjam-jam.",
      },
      {
        title: "Caption dalam hitungan detik",
        description:
          "Setiap klip otomatis mendapat subtitle yang rapi dan bergaya, pembeda antara klip yang ditonton dan klip yang di-scroll lewat.",
      },
      {
        title: "Tahu angkamu",
        description:
          "View Tracker memantau postingan kamu di semua akun, jadi melacak performa bukan lagi urusan spreadsheet.",
      },
      {
        title: "Tidak perlu latar belakang editing",
        description: "Kalau kamu bisa memilih momen yang bagus, sisanya Crayo yang urus.",
      },
      {
        title: "Semua di satu tab",
        description:
          "Campaign, clipping, caption, dan pelacakan ada di satu aplikasi, tanpa bolak-balik tool dari mencari kerjaan sampai mengirimkannya.",
      },
    ],
    relatedFeatures: ["auto-clip", "video-cutter", "video-crop", "youtube-downloader"],
    faqs: [
      {
        question: "Bagaimana cara menemukan campaign clipping?",
        answer:
          "Sudah terintegrasi di Content Library Crayo. Buka, lihat mana yang sedang aktif, dan mulai ngeklip. Video sumber disediakan oleh campaign.",
      },
      {
        question: "Bagaimana cara pembayarannya?",
        answer:
          "Setiap campaign menetapkan syarat dan reward-nya sendiri, ditampilkan di awal sebelum kamu mulai ngeklip. Baca detail campaign, potong klip, posting, dan dapatkan reward sesuai syaratnya.",
      },
      {
        question: "Perlu pengalaman editing?",
        answer:
          "Tidak. Auto Clip menemukan momennya, subtitle otomatis, dan editor mengurus potongan dan crop. Selera lebih penting daripada skill teknis.",
      },
      {
        question: "Bisa ngeklip kreator yang tidak punya campaign?",
        answer:
          "Bisa. Tempel link YouTube apa pun atau upload footage dan potong dengan cara yang sama. Campaign hanya jalur tercepat untuk dibayar.",
      },
    ],
  },
  "podcast-clipping": {
    related: [
      "/compare/crayo-vs-descript",
      "/compare/crayo-vs-riverside-magic-clips",
      "/features/auto-clip",
      "/features/speech-enhancer",
      "/use-cases/agencies",
      "/calculators/clips-from-one-video",
    ],
    navTitle: "Klip Podcast",
    seoTitle: "Generator Klip Podcast: Episode Jadi Shorts",
    seoDescription:
      "Ubah tiap episode podcast jadi klip untuk seminggu. AI cari momen terbaik, bersihkan audio, tambah caption, dan crop untuk TikTok, Reels, dan Shorts.",
    heroPrefix: "Ubah satu episode jadi",
    heroHighlight: "klip seminggu penuh",
    heroSuffix: "",
    description:
      "Masukkan episode, atau cukup link YouTube, dan biarkan AI menemukan momen yang layak diposting. Caption, crop vertikal, dan audio bersih sudah termasuk.",
    buttonText: "Potong episode gratis",
    buttonLink: "https://crayo.ai/create/auto-clip",
    steps: [
      {
        title: "Tambahkan episodemu",
        description:
          "Upload file atau tempel link YouTube dengan downloader bawaan. Episode full juga boleh.",
      },
      {
        title: "Biarkan AI mencari momennya",
        description:
          "Auto Clip memindai seluruh percakapan dan menarik bagian yang paling mungkin bikin orang berhenti scroll.",
      },
      {
        title: "Poles dan posting",
        description:
          "Speech enhancer membersihkan audio, caption diterapkan otomatis, dan setiap klip di-crop ke 9:16 untuk TikTok, Reels, dan Shorts.",
      },
    ],
    benefits: [
      {
        title: "Berhenti menyisir timeline",
        description:
          "Satu jam percakapan, dipindai dalam hitungan menit. Kamu meninjau klip, bukan footage mentah.",
      },
      {
        title: "Audio podcast, dibereskan",
        description:
          "Speech enhancer membersihkan gema ruangan dan level yang naik turun supaya klip terdengar seperti versi studio.",
      },
      {
        title: "Caption yang menopang klip",
        description:
          "Kebanyakan penonton video pendek nonton tanpa suara. Setiap klip keluar dengan subtitle yang akurat dan bergaya.",
      },
      {
        title: "Vertikal tanpa crop yang canggung",
        description:
          "Reframe episode horizontal ke 9:16 dengan rapi, bukan memposting video letterbox yang tidak ditonton siapa pun.",
      },
      {
        title: "Cukup dari link",
        description:
          "Tanpa export dari hosting podcast kamu. Tempel URL YouTube dan mulai ngeklip.",
      },
      {
        title: "Bukan cuma podcast",
        description:
          "Webinar, sesi coaching, materi kursus, wawancara: selama itu konten panjang berisi obrolan, Crayo bisa memotongnya.",
      },
    ],
    relatedFeatures: ["auto-clip", "speech-enhancer", "youtube-downloader", "video-crop"],
    faqs: [
      {
        question: "Bagaimana Crayo tahu momen mana yang harus dipotong?",
        answer:
          "Auto Clip menganalisis seluruh episode dan memunculkan bagian dengan hook terkuat, seperti cerita, opini panas, dan punchline, lalu kamu pilih mana yang mau diposting.",
      },
      {
        question: "Saya coach atau pembuat kursus, bukan podcaster. Apakah cocok untuk konten saya?",
        answer:
          "Cocok. Semua konten panjang berbasis obrolan, dari webinar dan workshop sampai modul kursus dan call dengan klien, dipotong dengan cara yang persis sama.",
      },
      {
        question: "Bisa langsung ngeklip dari YouTube?",
        answer:
          "Bisa. Tempel link-nya, Crayo menarik videonya dengan downloader bawaan, dan dalam hitungan menit kamu sudah ngeklip. Potong hanya konten yang haknya kamu miliki.",
      },
      {
        question: "Seperti apa klip jadinya?",
        answer:
          "Video vertikal 9:16 dengan subtitle bergaya yang sudah tertanam dan audio yang sudah dibersihkan, siap diposting ke TikTok, Reels, dan Shorts apa adanya.",
      },
    ],
  },
  streamers: {
    related: [
      "/features/auto-clip",
      "/features/video-crop",
      "/use-cases/clippers",
      "/calculators/clips-from-one-video",
      "/compare/crayo-vs-eklipse",
      "/compare/crayo-vs-streamladder",
    ],
    navTitle: "Streamer",
    seoTitle: "Pembuat Klip Twitch dan Kick untuk Streamer",
    seoDescription:
      "Ubah VOD stream jadi shorts yang menumbuhkan channel. AI menemukan momen hype, menambah caption, dan memformat klip untuk TikTok, Reels, dan Shorts.",
    heroPrefix: "Ubah",
    heroHighlight: "VOD stream",
    heroSuffix: "jadi shorts yang menumbuhkan channel",
    description:
      "Crayo mengubah VOD Twitch dan Kick jadi klip vertikal pendek dengan mencari momen paling berenergi dan memformatnya untuk TikTok, Reels, dan Shorts. Cocok untuk streamer yang mau klip dibuat dari footage yang sudah mereka rekam.",
    buttonText: "Potong VOD gratis",
    buttonLink: "https://crayo.ai/create/auto-clip",
    steps: [
      {
        title: "Masukkan VOD",
        description:
          "Upload VOD Twitch atau Kick kamu, atau ambil export stream dari YouTube dengan downloader bawaan.",
      },
      {
        title: "AI mencari momen hype",
        description:
          "Auto Clip memindai stream berjam-jam dan menarik clutch play, momen rage, dan bagian yang bikin chat meledak.",
      },
      {
        title: "Format vertikal dan posting",
        description:
          "Layout streamer dengan facecam di depan dan tengah, caption otomatis, crop 9:16, export.",
      },
    ],
    benefits: [
      {
        title: "VOD kamu adalah tambang konten",
        description:
          "Setiap stream sudah berisi klip pendek terbaikmu. Crayo menggalinya supaya tidak mati begitu saja di arsip.",
      },
      {
        title: "Ngeklip setelah tidur, bukan menggantikan tidur",
        description:
          "Streaming enam jam lalu editing tiga jam adalah cara streamer burnout. Auto Clip yang menyisir untukmu.",
      },
      {
        title: "Layout facecam di depan",
        description:
          "Format khusus streamer: kamera kamu dan gameplay, ditata untuk layar vertikal.",
      },
      {
        title: "Bereskan audio yang berantakan",
        description:
          "Speech enhancer menjinakkan bocoran audio game dan mic yang tidak konsisten supaya komentar kamu benar-benar terdengar.",
      },
      {
        title: "Shorts adalah mesin discovery kamu",
        description:
          "TikTok dan Shorts menjangkau orang yang tidak akan pernah scroll direktori Twitch. Klip yang konsisten adalah cara streamer kecil ditemukan.",
      },
      {
        title: "Posting selagi momennya masih panas",
        description:
          "Potong stream malam ini dalam hitungan menit, supaya shorts-nya naik saat chat masih membicarakannya.",
      },
    ],
    relatedFeatures: ["auto-clip", "speech-enhancer", "video-crop", "video-cutter"],
    faqs: [
      {
        question: "Apakah berfungsi untuk Twitch dan Kick?",
        answer:
          "Ya. Masukkan VOD dari platform mana pun (atau arsip stream YouTube kamu) dan potong dengan cara yang sama.",
      },
      {
        question: "Stream saya 4 jam lebih. Sanggup?",
        answer:
          "Justru itu intinya. Auto Clip dibuat untuk footage panjang. Ia memindai seluruh VOD dan menyerahkan momen-momennya ke kamu, jadi durasi panjang itu aset, bukan beban.",
      },
      {
        question: "Apakah klip benar-benar menumbuhkan stream?",
        answer:
          "Video pendek adalah tempat penonton baru menemukan streamer yang belum besar. Klip tidak menggantikan stream yang bagus, tapi itu sumber penonton baru paling andal yang dimiliki streamer kecil.",
      },
      {
        question: "Perlu facecam di setiap klip?",
        answer:
          "Tidak. Layout streamer itu opsional, jadi kamu bisa pakai gameplay full-screen, facecam di depan, atau layout split di tiap klip.",
      },
    ],
  },
};
